import { Box, TableHead, TableCell, TableRow, TableBody } from "@mui/material";
import { Link } from "react-router-dom";
import { AdvisorTitle } from "../components/AdvisorTitle";
import { useDealContext } from "../../deal/context";
import { useAdvisor } from "../context/AdvisorContext";
import { AdvisorTable } from "../../company/styles/AdvisorStyles";
import { IViewpoint, IFeedback, ITouchpoint, ViewpointStatus, TouchpointType } from "../../../types";
import dayjs from "dayjs";
import Loading from "../../../components/Loading";
import { ActiveAdvisors } from "../components/ActiveAdvisors";
import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import ClientProvider from "../../../modules/ClientProvider";

export const AdvisorFeedback = () => {
    const { dealContext } = useDealContext();
    const { advisor, loading, fetchAdvisor } = useAdvisor();
    const project = dealContext.project;
    const viewerRole = dealContext.role;
    const [feedback, setFeedback] = useState<IFeedback[]>([]);
    const [feedbackLoading, setFeedbackLoading] = useState(false);
    const { getAccessTokenSilently } = useAuth0();

    const [touchpoints, setTouchpoints] = useState<ITouchpoint[]>([]);
    const [touchpointsLoading, setTouchpointsLoading] = useState(false);


    const fetchAdvisorTouchpoints = async () => {
        setTouchpointsLoading(true);
        const accessToken = await getAccessTokenSilently();
        const touchpointClient = ClientProvider.provideAdvisorTouchpointClient();
        const { data, error } = await touchpointClient.getAdvisorTouchpoints(project.tag, accessToken);
        if (data?.touchpoints) {
            setTouchpoints(data.touchpoints);
        }
        setTouchpointsLoading(false);
    };


    // Fetch advisor feedback when advisor is loaded
    useEffect(() => {
        const fetchAdvisorFeedback = async () => {
            if (advisor?.id && project?.tag) {
                setFeedbackLoading(true);
                try {
                    const accessToken = await getAccessTokenSilently();
                    const feedbackClient = ClientProvider.provideAdvisorFeedbackClient();
                    const { data, error } = await feedbackClient.getAdvisorFeedback(
                        project.tag, 
                        advisor.id, 
                        accessToken
                    );
                    if (data?.feedback) {
                        setFeedback(data.feedback);
                    }
                } catch (error) {
                    console.error('Error fetching advisor feedback:', error);
                } finally {
                    setFeedbackLoading(false);
                }
            }
        };

        fetchAdvisorFeedback();
        fetchAdvisorTouchpoints();
    }, [advisor?.id, project?.tag]);

    if (loading) {
        return <Loading />;
    }

    const visibleTouchpoints = touchpoints.filter((touchpoint: ITouchpoint) => (touchpoint?.status === "active" && touchpoint?.type !== TouchpointType.DEFAULT) || touchpoint?.viewpoints?.[0]);
    return (
        <Box>
            <AdvisorTitle project={project} section="Feedback" subheading={`React to product demos, participate in market surveys, and provide tips and insights along the way.`} />

            <Box sx={{ display: "flex", flexDirection: "row", gap: "40px" }}>
                <Box sx={{ flex: "1 1" }}>
                    <h5>Demos and Surveys</h5>

                    <AdvisorTable sx={{ marginBottom: "60px", borderTop: "1px solid #000000", marginTop: "20px" }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {touchpointsLoading ? (
                                <TableRow>
                                    <TableCell colSpan={3} sx={{ textAlign: 'center', py: 3 }}>
                                        Loading touchpoints...
                                    </TableCell>
                                </TableRow>
                            ) : visibleTouchpoints.length > 0 ? (
                                visibleTouchpoints.map((touchpoint: ITouchpoint) => { 
                                    let status = touchpoint?.viewpoints?.[0]?.status ? touchpoint?.viewpoints[0]?.status.charAt(0).toUpperCase() + touchpoint?.viewpoints[0]?.status.slice(1).replace("_", " ") : "New";
                                    if (status === "Approved") {
                                        status = "Completed";
                                    }
                                    return (
                                    <TableRow key={touchpoint.uuid}>
                                        <TableCell>{dayjs(touchpoint.created_at).format('MMM D, YYYY')}</TableCell>
                                        <TableCell><Link to={`/advisor/feedback/${touchpoint.uuid}`}>{touchpoint.name}</Link></TableCell>
                                        <TableCell>{status}</TableCell>
                                    </TableRow>
                                )})
                            ) : (
                                   <TableRow>
                                        <TableCell colSpan={3} sx={{ textAlign: 'center', py: 3, color: '#666' }}>
                                        No touchpoints found. Share your ideas and insights to help improve the product!
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </AdvisorTable>
                    { feedback.length > 0 && (
                        <Box>

                    <h5>Ideas and Tips</h5>
                    <Box sx={{ display: "flex", flexDirection: "row", gap: "40px", marginTop: "20px" }}>
                        <Box sx={{ flex: "1 1" }}>
                            <AdvisorTable sx={{ borderTop: "1px solid #000000" }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Summary</TableCell>
                                        <TableCell>Category</TableCell>
                                        <TableCell>Detail</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {feedbackLoading ? (
                                        <TableRow>
                                            <TableCell colSpan={3} sx={{ textAlign: 'center', py: 3 }}>
                                                Loading feedback...
                                            </TableCell>
                                        </TableRow>
                                    ) : feedback.length > 0 ? (
                                        feedback.map((feedbackItem: IFeedback) => (
                                            <TableRow key={feedbackItem.uuid}>
                                                <TableCell>
                                                    {dayjs(feedbackItem.submitted_at || feedbackItem.created_at).format('MMM D, YYYY')}
                                                </TableCell>
                                                <TableCell>
                                                    <strong>{feedbackItem.title}</strong>

                                                </TableCell>
                                                <TableCell>
                                                {feedbackItem.type.charAt(0).toUpperCase() + feedbackItem.type.slice(1)}
                                                </TableCell>
                                                <TableCell>
                                                    {feedbackItem.content.length > 100 
                                                        ? `${feedbackItem.content.substring(0, 100)}...` 
                                                        : feedbackItem.content
                                                    }
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={3} sx={{ textAlign: 'center', py: 3, color: '#666' }}>
                                                No feedback submitted yet. Share your ideas and insights to help improve the product!
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </AdvisorTable>
                        </Box>
                    </Box>
                    </Box>
                    )}
                </Box>
                <Box sx={{ flex: "0 1 250px" }}>
                    <ActiveAdvisors advisor={advisor} project={project} />
                </Box>
            </Box>


        </Box>
    );
}